import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import type { RemovalMode } from './types';

export interface TypeToConfirmDialogProps {
  /** Owned by the caller — this component has no internal open state. */
  open: boolean;
  /** The org name the user must type exactly (case-sensitive) to confirm. */
  orgName: string;
  /** Names the pending action for accessible naming (title/description). */
  mode: RemovalMode;
  /**
   * When set, the action is scoped to this team (not the whole org) — shown
   * in the title and description so the user knows which library a wipe or
   * replace will touch. Omit for org-wide actions.
   */
  scopeName?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  /** True while the removal mutation (and refetch) is in flight. */
  isSubmitting?: boolean;
  /** Shown inside the dialog when the confirm action fails (table is behind the modal). */
  error?: string | null;
}

const TITLE_ID = 'type-to-confirm-dialog-title';
const DESCRIPTION_ID = 'type-to-confirm-dialog-description';

const MODE_LABELS: Record<RemovalMode, string> = {
  wipe: 'Wipe entire library',
  select_only: 'Remove selected exercises',
  import_only: 'Import only',
  replace: 'Replace',
};

export function TypeToConfirmDialog({
  open,
  orgName,
  mode,
  scopeName,
  onConfirm,
  onCancel,
  isSubmitting = false,
  error = null,
}: TypeToConfirmDialogProps) {
  const [typedText, setTypedText] = useState('');

  // Guards against stale input if the dialog is reopened for a different
  // org/mode without the caller having unmounted it in between.
  useEffect(() => {
    if (!open) {
      setTypedText('');
    }
  }, [open]);

  const modeLabel = MODE_LABELS[mode];
  const scopeSuffix = scopeName ? ` — ${scopeName}` : '';
  const matches = typedText === orgName;
  const showMismatch = typedText.length > 0 && !matches;

  let confirmLabel = 'Confirm';
  if (isSubmitting) {
    confirmLabel = error ? 'Retrying...' : 'Working...';
  } else if (error) {
    confirmLabel = 'Retry';
  }

  const handleCancel = () => {
    if (isSubmitting) return;
    setTypedText('');
    onCancel();
  };

  const handleConfirm = () => {
    if (!matches || isSubmitting) {
      return;
    }
    onConfirm();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      aria-labelledby={TITLE_ID}
      aria-describedby={DESCRIPTION_ID}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id={TITLE_ID}>
        Confirm removal for {orgName}
        {' — '}
        {modeLabel}
        {scopeSuffix}
      </DialogTitle>
      <DialogContent>
        <Typography id={DESCRIPTION_ID} gutterBottom>
          This action cannot be undone
          {scopeName ? ` (scoped to ${scopeName})` : ''}. Type &quot;{orgName}
          &quot; below to confirm.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          margin="normal"
          label={`Type "${orgName}" to confirm`}
          value={typedText}
          onChange={(event) => setTypedText(event.target.value)}
          error={showMismatch}
          disabled={isSubmitting}
        />
        <Typography
          role="status"
          aria-live="polite"
          color="error"
          variant="body2"
          // Rendered (not visually-hidden) so the mismatch is visible to
          // sighted users too, while aria-live announces it to screen
          // readers as it changes.
        >
          {showMismatch
            ? `The text you entered doesn't match "${orgName}".`
            : ''}
        </Typography>
        {error ? (
          <Alert severity="error" className="mt-3">
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={!matches || isSubmitting}
          onClick={handleConfirm}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
